import { useEffect, useState } from "react";
import {
    Box,
    FormControlLabel,
    IconButton,
    Popover,
    Radio,
    RadioGroup,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import { Cached, HelpOutline } from "@mui/icons-material";
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { okaidia } from '@uiw/codemirror-theme-okaidia';
import { defaultTemplateHTML } from "../../../../../types/template";
import TemplatePreview from "../../../../Shared/TemplatePreview";
import { getFilesFromURLs } from "../../../../../hooks/getFilesFromURLs";

const title = "Join Statrat";
const headline = "Statrat is a free social stat tracking platform for ball players. Download now to track your own stats.";
const imageURLs = [
    "https://picsum.photos/200/300.jpg",
    "https://picsum.photos/200.jpg",
    "https://picsum.photos/400/200.jpg"
];
const videoURLs = [
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4"
];

const HTMLInput = ({ code, setCode }: { code: string, setCode: (v: string) => void }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const showHelp = Boolean(anchorEl);
    const helpId = showHelp ? 'help-popover' : undefined;

    const [showPreview, setShowPreview] = useState(true);
    const [usePlaceholders, setUsePlaceholders] = useState(false);
    const [mediaType, setMediaType] = useState("photos");
    
    const [media, setMedia] = useState<File[]>([]);

    useEffect(() => {
        async function updateMedia() {
            var files = [];
            if (mediaType === "photos") {
                files = await getFilesFromURLs(imageURLs, ["image1", "image2", "image3"]);
            } else {
                files = await getFilesFromURLs(videoURLs, ["video1", "video2", "video3"]);
            }
            setMedia(files);
        }

        if (usePlaceholders) {
            updateMedia();
        } else {
            setMedia([]);
        }
    }, [usePlaceholders, mediaType])

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Template HTML</Typography>
                <Box display="flex">
                    <Tooltip title="Load Sample Code">
                        <IconButton onClick={() => setCode(defaultTemplateHTML)}>
                            <Cached />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Help">
                        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} aria-describedby={helpId}>
                            <HelpOutline />
                        </IconButton>
                    </Tooltip>
                    <Popover
                        id={helpId}
                        open={showHelp}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Typography sx={{ p: 2 }} component="pre" style={{ wordWrap: "break-word" }}>
                            Input the HTML code that will be used to generate the ad.{"\n"}
                            Use the following placeholders inside the code for user uploaded content:{"\n"}
                            {"\n"}
                            - {"{{title}}"}: Title for the ad (less than 30 characters){"\n"}
                            - {"{{headline}}"}: Headline that adds more info for the ad (less than 100 characters){"\n"}
                            - {"{{media1}}"}, {"{{media2}}"}, {"{{media3}}"}: URLs for the ad media, can be an image or video{"\n"}
                            - {"{{mediaType1}}"}, {"{{mediaType2}}"}, {"{{mediaType3}}"}: Used to determine if media is an image or video in dashboard template previews{"\n"}
                            {"\n"}
                            Users can only upload up to 3 media files.{"\n"}
                            For template previews to work for users in the dashboard, also make sure to create the four following scripts:{"\n"}
                            {"\n"}
                            - {"<script id='title-script'>"}: Generates title component of ad{"\n"}
                            - {"<script id='headline-script'>"}: Generates headline component of ad{"\n"}
                            - {"<script id='media-script'>"}: Generates media component of ad{"\n"}
                            - {"<script id='slides-script'>"}: Generates transitions and slideshow aspect of ad if required{"\n"}
                            {"\n"}
                            To view an example, load the sample code to see how these scripts should be setup.
                        </Typography>
                    </Popover>
                </Box>
            </Box>
            <Box display="flex" gap={2}>
                <Box width="100%" display="flex" flexDirection="column" overflow="auto">
                    <CodeMirror
                        value={code}
                        height="800px"
                        extensions={[html()]}
                        theme={okaidia}
                        placeholder={"Enter HTML for the template."}
                        style={{ position: "relative" }}
                        onChange={(val) => { setCode(val) }}
                    />
                </Box>
                <TemplatePreview
                    template={code}
                    title={usePlaceholders ? title : ""}
                    headline={usePlaceholders ? headline : ""}
                    media={media}
                    visible={showPreview}
                />
            </Box>
            <Box display="flex" gap={3}>
                <FormControlLabel
                    control={<Switch checked={showPreview} onChange={(v) => setShowPreview(v.target.checked)} />}
                    label="Show Preview"
                />
                <FormControlLabel
                    control={<Switch checked={usePlaceholders} onChange={(v) => setUsePlaceholders(v.target.checked)} />}
                    label="Fill Placeholders with Sample Data"
                />
                <RadioGroup row defaultValue="photos" value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
                    <FormControlLabel value="photos" control={<Radio />} label="Photos" />
                    <FormControlLabel value="video" control={<Radio />} label="Video" />
                </RadioGroup>
            </Box>
        </Box>
    )
}

export default HTMLInput;
